/**
 * 重置密码 · 第 2 步：邮箱 + 6 位验证码 + 新密码。
 *
 * 几个与后端契约绑在一起的细节：
 *  - **验证码一次性**：`consumeEmailCode` 用过即失效，所以"新密码太弱"被拒之后
 *    同一枚码也不能再用（见 `auth.service.confirmPasswordReset`），用户要重新收信。
 *    这不是 bug，是刻意的 —— 否则一枚码可以换来无数次尝试。
 *  - **改密成功后后端吊销全部会话**，所以要回登录页重新登录。
 *  - **TOTP 不受影响**：开启了动态口令的家长，用新密码登录后**仍然**要输 6 位动态码。
 *    这一点必须在界面上说清楚，否则用户会以为"重置失败、怎么还要验证码"。
 *    （丢了认证器的人不走这条路，见 `docs/接口契约对齐.md` 的「紧急恢复」。）
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PARENT_MIN_PASSWORD_LENGTH,
  checkPasswordStrength,
  emailSchema,
} from '@fht/shared';
import * as authApi from '../../lib/api/auth';
import { ApiError, toErrorMessage } from '../../lib/api/error';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { CodeInput } from '../../components/ui/CodeInput';
import { PasswordStrengthMeter } from '../../components/ui/PasswordStrengthMeter';
import { BuildStamp } from '../../components/layout/BuildStamp';
import { formatCountdown } from '../../lib/utils';

/** 与后端 `RESET_EMAIL_COOLDOWN_SECONDS` 一致。 */
const RESEND_SECONDS = 60;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialEmail = useMemo(
    () => (params.get('email') ?? '').trim().toLowerCase(),
    [params],
  );

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = globalThis.setInterval(() => {
      setCountdown((v) => (v <= 1 ? 0 : v - 1));
    }, 1000);
    return () => globalThis.clearInterval(timer);
  }, [countdown]);

  const passwordCheck = newPassword
    ? checkPasswordStrength(newPassword, {
        minLength: PARENT_MIN_PASSWORD_LENGTH,
        identities: [email],
      })
    : null;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    const normalized = email.trim().toLowerCase();
    const parsedEmail = emailSchema.safeParse(normalized);
    if (!parsedEmail.success) {
      setError(parsedEmail.error.issues[0]?.message ?? '请输入正确的邮箱地址');
      return;
    }
    if (code.length !== 6) {
      setError('请输入邮件里的 6 位验证码');
      return;
    }
    if (!newPassword) {
      setError('请设置新密码');
      return;
    }
    if (passwordCheck && !passwordCheck.ok) {
      setError(passwordCheck.reason ?? '密码强度不足');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.confirmPasswordReset(parsedEmail.data, code, newPassword);
      setDone(true);
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setError(null);
    setInfo(null);
    const normalized = email.trim().toLowerCase();
    const parsedEmail = emailSchema.safeParse(normalized);
    if (!parsedEmail.success) {
      setError(parsedEmail.error.issues[0]?.message ?? '请先填写正确的邮箱地址');
      return;
    }
    setResending(true);
    try {
      await authApi.requestPasswordReset(parsedEmail.data);
      // 与第 1 步同一个口径：不透露邮箱是否注册，只描述"我们做了什么"
      setInfo('如果该邮箱已注册，新验证码已发出，请查收（也看一下垃圾邮件箱）');
      setCountdown(RESEND_SECONDS);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(`发送太频繁了，请等 ${RESEND_SECONDS} 秒后再试一次`);
      } else {
        setError(toErrorMessage(err));
      }
    } finally {
      setResending(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
        {/*
          成功态原来把卡片直接当居中容器里的唯一子元素；这里多套一层 `w-full max-w-md`
          的包裹，好让「构建于 …」与另外 4 个未登录页落在**同一个位置关系**上
          （居中卡片下方居中）。卡片本身仍是 `w-full`，窄屏与长文案行为不变。
        */}
        <div className="w-full max-w-md">
          <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <h1 className="text-xl font-bold text-slate-900">密码已重置</h1>
            <p className="mt-2 text-sm text-slate-500">
              为安全起见，其它设备上的登录已全部退出，请用新密码重新登录。
            </p>
            <p className="mt-2 text-sm text-slate-500">
              如果你开启过<b>动态口令</b>，登录时仍然需要输入认证器 App 上的 6 位数字
              —— 换密码不等于换认证器。
            </p>
            <Button className="mt-5" fullWidth size="lg" onClick={() => navigate('/login')}>
              去登录
            </Button>
          </div>

          <BuildStamp className="mt-4 text-center" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900">重置密码</h1>
          <p className="mt-1 text-sm text-slate-500">输入邮件里的 6 位验证码，并设置新密码</p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <Input
            label="邮箱"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            inputMode="email"
            hint="必须与收到验证码的那个邮箱一致"
          />

          <CodeInput value={code} onChange={setCode} label="验证码" />

          <div className="space-y-2">
            <Input
              label="新密码"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              hint={`至少 ${PARENT_MIN_PASSWORD_LENGTH} 位`}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-lg px-2 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50"
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? '隐藏' : '显示'}
                </button>
              }
            />
            <PasswordStrengthMeter password={newPassword} />
            {passwordCheck && !passwordCheck.ok && (
              <p className="text-xs text-amber-600">{passwordCheck.reason}</p>
            )}
          </div>

          {error && (
            <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
              {error}
            </div>
          )}
          {info && (
            <div role="status" className="rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
              {info}
            </div>
          )}

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            重置密码
          </Button>

          <div className="flex items-center justify-between gap-2 text-sm">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void onResend()}
              loading={resending}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `重新发送（${formatCountdown(countdown)}）` : '重新发送验证码'}
            </Button>
            <Link to="/login" className="shrink-0 font-medium text-brand-600 hover:underline">
              返回登录
            </Link>
          </div>
        </form>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          表单态与上面的成功态、以及另外 4 个未登录页用的是**同一个**位置关系：
          居中卡片（这里是表单本身）下方的页脚处居中。复用全站唯一的 `BuildStamp`
          （见 lib/buildStamp.ts），不另写实现、不复制样式。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}

